import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  TagManyWithCountsDocument,
  useTagCreateMutation,
} from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createMultilingual } from "shared/utils";
import { usePostHog } from "shared/hooks";

export const CREATE_TAG_ENTITY_OPTIONS = [
  { value: EnumTagEntityTypes.Operator, labelKey: "CREATE_LISTINGS" },
  { value: EnumTagEntityTypes.Event, labelKey: "CREATE_EVENTS" },
  { value: EnumTagEntityTypes.Promotion, labelKey: "CREATE_PROMOTIONS" },
  { value: EnumTagEntityTypes.Person, labelKey: "CREATE_INQUIRIES" },
  { value: EnumTagEntityTypes.Organization, labelKey: "CREATE_MEDIA" },
] as const;

type UseCreateCustomTagModalProps = {
  onClose: () => void;
  tagGroupId: string;
  mediaGalleryTagGroupId?: string;
  organizationId: string;
};

export const useCreateCustomTagModal = ({
  onClose,
  tagGroupId,
  mediaGalleryTagGroupId,
  organizationId,
}: UseCreateCustomTagModalProps) => {
  const [tagName, setTagName] = useState("");
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<
    EnumTagEntityTypes[]
  >([]);
  const [inheritable, setInheritable] = useState(false);

  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const [createTag] = useTagCreateMutation({
    refetchQueries: [TagManyWithCountsDocument],
  });

  const handleClose = () => {
    setTagName("");
    setSelectedEntityTypes([]);
    setInheritable(false);
    onClose();
  };

  const toggleEntityType = (entityType: EnumTagEntityTypes) => {
    setSelectedEntityTypes(prev => {
      const isSelected = prev.includes(entityType);
      if (isSelected) {
        return prev.filter(t => t !== entityType);
      }
      // Media tags live in a different tag group — keep selection exclusive.
      if (entityType === EnumTagEntityTypes.Organization) {
        return [EnumTagEntityTypes.Organization];
      }
      return [
        ...prev.filter(t => t !== EnumTagEntityTypes.Organization),
        entityType,
      ];
    });
  };

  const [isLoading, setIsLoading] = useState(false);

  const isMediaTag = selectedEntityTypes.includes(
    EnumTagEntityTypes.Organization,
  );

  const canSave =
    tagName.trim().length > 0 &&
    selectedEntityTypes.length > 0 &&
    (!isMediaTag || !!mediaGalleryTagGroupId);

  const handleSaveTag = async () => {
    if (!canSave) return;
    const resolvedTagGroupId = isMediaTag ? mediaGalleryTagGroupId : tagGroupId;
    if (!resolvedTagGroupId) return;

    try {
      setIsLoading(true);
      await createTag({
        variables: {
          record: {
            entityTypes: isMediaTag
              ? [EnumTagEntityTypes.Organization]
              : selectedEntityTypes,
            name: createMultilingual(tagName.trim()),
            owner: {
              entityId: organizationId,
              type: EnumOwnerType.Organization,
            },
            tagGroup: resolvedTagGroupId,
            inheritable: isMediaTag ? false : inheritable,
          },
        },
      });

      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t(
          `home:VIEWS.SETTINGS.VIEWS.CUSTOM_TAGS.CREATED.SUCCESS`,
          {
            tagName,
          },
        ),
      });
    } catch {
      trackPostHogException(new Error("Error creating custom tag"), {
        fileName: "useCreateCustomTagModal.ts",
        method: "handleSaveTag",
        message: "Error creating custom tag",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t(`home:VIEWS.SETTINGS.VIEWS.CUSTOM_TAGS.CREATED.ERROR`),
      });
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  const isListingTag = selectedEntityTypes.includes(
    EnumTagEntityTypes.Operator,
  );

  return {
    isLoading,
    tagName,
    setTagName,
    selectedEntityTypes,
    toggleEntityType,
    inheritable,
    setInheritable,
    isListingTag,
    isMediaTag,
    canSave,
    onCloseModal: handleClose,
    onSaveTag: handleSaveTag,
  };
};
